import React, { useEffect, useMemo } from 'react';
import { logger } from '@utils/logger';
import { find, isEmpty, isNil } from 'lodash';
import { ContentMatrix } from '@components/content-matrix';
import { restaurantPropTypes } from '@nandosaus/prop-types';
import PropTypes from 'prop-types';
import { MARKET } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { RestaurantModel, useRootStore } from '@nandosaus/state-management';
import { useFeature } from '@hooks/use-feature';
import { ContentImageBlock } from '@components/content-image';
import { ContentBlock } from '@components/content-block';
import { useMarket } from '@hooks/use-market';
import { DeliveryOptionsBlock } from '../delivery-options-block';
import { RestaurantDetailsTables } from '../restaurant-details-tables';
import { RestaurantOrderButton } from '../restaurant-order-button';
import { RestaurantPageHeader } from '../restaurant-page-header';

const CONTENT_IMAGE = [
  {
    url: '/static/images/restaurant/catering.jpg',
    alt: 'Restaurant Catering Packages',
  },
];

const VIEW_BUTTON = {
  text: 'View our catering packages',
  href: 'https://catering.nandos.com.au/',
};

const RestaurantPageContent = ({ restaurant, restaurantPageContent }) => {
  const { RestaurantDetailsState, RestaurantStore } = useRootStore();

  const showCmsRestaurantPages = useFeature('show-cms-restaurant-pages');

  const currentMarket = useMarket();

  const chosenRestaurant = useMemo(() => {
    if (RestaurantDetailsState.restaurant) {
      return RestaurantDetailsState.restaurant;
    }

    return RestaurantModel.create(restaurant);
  }, [RestaurantDetailsState.restaurant, restaurant]);

  const imageUrl = `https://assets.nandos.com.au/Nandos-${
    currentMarket === MARKET.AUSTRALIA ? 'Australia' : 'New-Zealand'
  }/images/Restaurant-Images/${chosenRestaurant.id}-restaurant-hero-header.png`;

  const headerImage = { url: imageUrl, alt: `Nandos ${chosenRestaurant.name} building image` };

  useEffect(() => {
    const updateDynamicRestaurantData = async () => {
      await RestaurantStore.loadRestaurants();

      const selected = find(RestaurantStore.asArray, ({ id }) => id === restaurant.id);

      if (selected) {
        RestaurantDetailsState.updateRestaurant(selected);
      } else {
        logger.error('Error updating dynamic restaurant data.', { restaurant });
      }
    };

    updateDynamicRestaurantData();
  }, [RestaurantDetailsState, RestaurantStore, restaurant, restaurant.id]);

  const content =
    restaurantPageContent && showCmsRestaurantPages
      ? {
          heading: restaurantPageContent.heading.replace('$restaurantName', chosenRestaurant.name),
          subheading: restaurantPageContent.subheading?.replace('$restaurantName', chosenRestaurant.name),
          contentMatrix: restaurantPageContent.contentMatrix,
          contentBlock: {
            blockTitle: restaurantPageContent.description?.replace('$restaurantName', chosenRestaurant.name),
            body: restaurantPageContent.body?.replace('$restaurantName', chosenRestaurant.name),
          },
        }
      : {
          heading: `Nandos ${chosenRestaurant.name}`,
          subheading: undefined,
          contentMatrix: [],
          contentBlock: undefined,
        };

  return (
    <>
      <RestaurantPageHeader
        state={chosenRestaurant.address.state}
        heading={content.heading}
        subheading={content.subheading}
        backgroundImage={headerImage}
        restaurant={chosenRestaurant}
      />

      <RestaurantOrderButton restaurant={chosenRestaurant} />

      <RestaurantDetailsTables restaurant={chosenRestaurant} />

      {!isEmpty(chosenRestaurant.formattedDeliveryServices) && !isNil(chosenRestaurant.orderingEnabled) && (
        <DeliveryOptionsBlock
          deliveryServices={chosenRestaurant.formattedDeliveryServices}
          orderingEnabled={chosenRestaurant.orderingEnabled}
          hasBackgroundDecal={!showCmsRestaurantPages}
        />
      )}
      {!showCmsRestaurantPages && currentMarket === MARKET.AUSTRALIA && (
        <ContentImageBlock
          heading="Catering Packages"
          body="Need to feed the crew? Nando’s catering packages will spice up any event."
          image={CONTENT_IMAGE}
          button={VIEW_BUTTON}
        />
      )}
      {showCmsRestaurantPages && content.contentBlock?.blockTitle && <ContentBlock {...content.contentBlock} />}
      {showCmsRestaurantPages && !!content.contentMatrix.length && (
        <ContentMatrix blocks={restaurantPageContent.contentMatrix} />
      )}
    </>
  );
};

RestaurantPageContent.propTypes = {
  restaurantPageContent: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    restaurantId: PropTypes.number.isRequired,
    heading: PropTypes.string,
    subheading: PropTypes.string,
    description: PropTypes.string,
    body: PropTypes.string,
    contentMatrix: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }),
  restaurant: restaurantPropTypes.isRequired,
};

RestaurantPageContent.defaultProps = {
  restaurantPageContent: undefined,
};

export default observer(RestaurantPageContent);
